import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Google Analytics 4 — La Estrofa
const GA_ID = "G-C0DWWLBNKQ";
const CONSENT_KEY = "estrofa_cookie_consent_v1";
// Título por defecto del index.html: durante la transición de ruta, Helmet revierte a él
// un instante. Lo tratamos como "aún no listo" para no medir un título equivocado.
const DEFAULT_TITLE = "La Estrofa · Medio musical: actualidad, lanzamientos y entrevistas";

function readAnalyticsConsent() {
  try {
    return !!JSON.parse(localStorage.getItem(CONSENT_KEY))?.analytics;
  } catch {
    return false;
  }
}

// Carga gtag.js una sola vez, con Consent Mode v2 (denegado por defecto hasta aceptar analítica).
function loadGtagOnce() {
  if (window.__ga4_loaded) return;
  window.__ga4_loaded = true;

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: readAnalyticsConsent() ? "granted" : "denied",
    wait_for_update: 500,
  });
  // send_page_view:false → los page_view los enviamos nosotros por ruta (evita duplicados en SPA).
  gtag("config", GA_ID, { send_page_view: false });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
}

export function Analytics() {
  const location = useLocation();

  // Inicializa el tag y sincroniza el consentimiento con el banner de cookies.
  useEffect(() => {
    loadGtagOnce();
    const onConsent = (e) => {
      const granted = !!e.detail?.analytics;
      if (window.gtag) {
        window.gtag("consent", "update", { analytics_storage: granted ? "granted" : "denied" });
      }
    };
    window.addEventListener("cookie-consent-updated", onConsent);
    return () => window.removeEventListener("cookie-consent-updated", onConsent);
  }, []);

  // page_view en cada cambio de ruta (SPA), una sola vez por URL y con el título ya actualizado.
  useEffect(() => {
    const path = location.pathname + location.search;
    // El panel de administración no es público: no lo medimos.
    if (path.startsWith("/mazuecos")) return;

    // Un ÚNICO temporizador a nivel de módulo: cancelar cualquier medición pendiente de la
    // ruta anterior garantiza que nunca haya dos intervalos solapados (nada de page_view duplicados).
    if (window.__ga4_iv) { clearInterval(window.__ga4_iv); window.__ga4_iv = null; }

    const startTitle = document.title;
    let prev = startTitle;
    let stable = 0;
    let elapsed = 0;

    const send = () => {
      clearInterval(window.__ga4_iv);
      window.__ga4_iv = null;
      // Solo si seguimos en esta ruta (evita cruces de título/URL en navegaciones rápidas).
      if ((window.location.pathname + window.location.search) !== path) return;
      if (window.__ga4_lastPath === path) return; // no repetir el mismo page_view
      window.__ga4_lastPath = path;
      if (!window.gtag) return;
      window.gtag("event", "page_view", {
        page_location: window.location.origin + path,
        page_path: path,
        page_title: document.title,
      });
    };

    // Sondea document.title (robusto aunque react-helmet reemplace el <title>).
    // Envía cuando el título es el DE ESTA página: distinto al de la ruta anterior,
    // distinto al default (parpadeo de transición) y estable ~450ms. Tope duro a 2.5s.
    window.__ga4_iv = setInterval(() => {
      elapsed += 150;
      const t = document.title;
      if (t !== prev) { prev = t; stable = 0; } else { stable += 1; }
      const ready = !!t && t !== DEFAULT_TITLE && t !== startTitle;
      if ((ready && stable >= 3) || elapsed >= 2500) send();
    }, 150);
  }, [location.pathname, location.search]);

  return null;
}
